/**
 * Pure helpers for the bearer tokens issued by the Bhabi API.
 *
 * Room tokens are JWTs whose body carries `room_id` (see lib.Claims on the Go
 * side). Guest tokens additionally expire after 7 days. Nothing here verifies
 * signatures — it only reads claims so the UI can route and warn early. The
 * server stays the source of truth.
 */

export interface BhabiTokenClaims {
  id?: string;
  email?: string;
  room_id?: string;
  roomID?: string;
  exp?: number;
  userType?: string;
}

const NIL_UUID = '00000000-0000-0000-0000-000000000000';

const base64UrlDecode = (segment: string): string => {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    '='
  );
  if (typeof atob === 'function') {
    return atob(padded);
  }
  // Jest / node fallback
  return Buffer.from(padded, 'base64').toString('binary');
};

export const decodeTokenClaims = (
  token: string | null | undefined
): BhabiTokenClaims | null => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const json = base64UrlDecode(parts[1]);
    const claims = JSON.parse(json) as BhabiTokenClaims;
    if (!claims || typeof claims !== 'object') return null;
    return claims;
  } catch {
    return null;
  }
};

/** Room id encoded in a room-scoped token, or null for lobby/guest tokens. */
export const roomIdFromToken = (
  token: string | null | undefined
): string | null => {
  const claims = decodeTokenClaims(token);
  const roomID = claims?.room_id || claims?.roomID;
  if (!roomID || roomID === NIL_UUID) return null;
  return roomID;
};

/**
 * True when the token carries an `exp` claim in the past. Tokens without exp
 * (or that cannot be decoded, e.g. Firebase ID tokens handled elsewhere) are
 * treated as not expired so the server decides.
 */
export const isTokenExpired = (
  token: string | null | undefined,
  nowMs: number = Date.now(),
  skewSeconds = 30
): boolean => {
  const exp = decodeTokenClaims(token)?.exp;
  if (!exp) return false;
  return exp * 1000 <= nowMs + skewSeconds * 1000;
};

/**
 * The room endpoints historically answer `{ token }`; the new guest contract
 * documents a bare JSON string. Accept both.
 */
export const extractToken = (res: unknown): string | null => {
  if (typeof res === 'string' && res.length) return res;
  if (res && typeof res === 'object' && 'token' in res) {
    const t = (res as { token?: unknown }).token;
    return typeof t === 'string' && t.length ? t : null;
  }
  return null;
};
